import React from 'react';
import { X, Download, Save } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable PDF preview modal. Embeds the PDF via <iframe> so the browser's
 * native PDF.js viewer handles zoom, pagination, thumbnails.
 *
 * Two save buttons:
 *  - "Télécharger" : direct download to the browser's default folder, using the
 *    proper renamed filename (via the <a download> attribute).
 *  - "Enregistrer sous" : opens the native Windows "Save As" dialog via the
 *    File System Access API. The browser remembers the last folder used per-site,
 *    so the user picks once their NAS path and it's pre-selected next time.
 *
 * Props:
 *   - url: absolute URL of the PDF (must include token for auth)
 *   - title: header title
 *   - downloadFilename: filename suggested when user clicks Download / Save As
 *   - onClose: callback
 */
const PDFPreviewModal = ({ url, title = 'Aperçu PDF', downloadFilename = 'document.pdf', onClose }) => {
  const safeName = (downloadFilename || 'document.pdf').toLowerCase().endsWith('.pdf')
    ? downloadFilename
    : `${downloadFilename || 'document'}.pdf`;

  // ── Download (default folder) ──────────────────────────────────────────────
  const handleDownload = () => {
    // No target="_blank" so the `download` attribute is honored and the file
    // is saved with the correct name (not the blob UUID).
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Save As (Windows dialog) ──────────────────────────────────────────────
  const handleSaveAs = async () => {
    // File System Access API (Chrome 86+, Edge 86+). Falls back to standard
    // download for unsupported browsers (Firefox / Safari).
    if (!('showSaveFilePicker' in window)) {
      toast.info('Boîte de dialogue native non supportée par ce navigateur — téléchargement classique. Utilisez Chrome ou Edge pour choisir le dossier.');
      handleDownload();
      return;
    }
    try {
      const blob = await fetch(url).then((r) => r.blob());
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [{ description: 'Document PDF', accept: { 'application/pdf': ['.pdf'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      toast.success(`✅ Enregistré sous ${handle.name}`);
    } catch (e) {
      if (e?.name === 'AbortError') return; // user cancelled
      console.error(e);
      toast.error("Échec de l'enregistrement : " + (e.message || 'erreur inconnue'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex flex-col" data-testid="pdf-preview-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shadow">
        <h3 className="font-semibold text-slate-900 truncate">{title}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveAs}
            data-testid="pdf-save-as-btn"
            className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center gap-2"
            title="Choisir où enregistrer le fichier (NAS, dossier local…)"
          >
            <Save className="w-4 h-4" />
            Enregistrer sous
          </button>
          <button
            onClick={handleDownload}
            data-testid="pdf-download-btn"
            className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium flex items-center gap-2"
            title="Téléchargement direct dans le dossier par défaut"
          >
            <Download className="w-4 h-4" />
            Télécharger
          </button>
          <button
            onClick={onClose}
            data-testid="pdf-close-btn"
            className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
            title="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <iframe
        src={url}
        title={title}
        className="flex-1 bg-slate-900"
        style={{ border: 'none' }}
      />
    </div>
  );
};

export default PDFPreviewModal;
